import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { formatPrice, formatFlightPriceComponent, getCurrency, initializeCurrency } from './utils/currency';
import { getBaggageAllowance, formatBaggageForDisplay } from './utils/airlineBaggageDatabase';
import './PassengerInfoPage.css';

const PassengerInfoPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get flight data from navigation state - REQUIRED, no fallback
  const flight = location.state?.flight;
  
  // Get preserved passenger data and additional options from navigation state (if coming back from terms page)
  const preservedPassengerData = location.state?.passengerData;
  const preservedAdditionalOptions = location.state?.additionalOptions;
  const preservedAcceptedTerms = location.state?.acceptedTerms;

  // Log flight data and check what's available from API
  React.useEffect(() => {
    if (flight) {
      console.log('========================================');
      console.log('👤 PASSENGER INFO PAGE - API DATA CHECK');
      console.log('========================================');
      console.log('📋 Complete Flight Object:', flight);
      console.log('');
      console.log('✅ FROM API (Real Data):');
      console.log('  - Flight ID:', flight.id);
      console.log('  - Offer ID:', flight.offerId || flight.id);
      console.log('  - Airline:', flight.airline);
      console.log('  - Flight Number:', flight.flightNumber);
      console.log('  - Departure:', flight.departureAirport, flight.departureCity);
      console.log('  - Arrival:', flight.arrivalAirport, flight.arrivalCity);
      console.log('');
      console.log('🔵 CLASS INFORMATION (CRITICAL):');
      console.log('  - flight.travelClass:', flight.travelClass);
      console.log('  - flight.cabinClass:', flight.cabinClass);
      console.log('  - flight.travelClass type:', typeof flight.travelClass);
      console.log('  - flight.cabinClass type:', typeof flight.cabinClass);
      console.log('  - Will use:', flight.travelClass || flight.cabinClass || 'Business');
      console.log('');
      console.log('💰 PRICING (From API):');
      console.log('  - Price Object:', flight.price);
      console.log('  - Total Price:', flight.price?.total || flight.total || 'N/A');
      console.log('  - Base Fare:', flight.price?.base || flight.baseFare || '❌ NOT FROM API');
      console.log('  - Taxes:', flight.price?.taxes || flight.taxes || '❌ NOT FROM API');
      console.log('  - Original Currency:', flight.price?.originalCurrency || 'N/A');
      console.log('');
      console.log('🧳 BAGGAGE (From API):');
      console.log('  - Baggage Info:', flight.baggage || '❌ NOT FROM API');
      if (flight.baggage) {
        console.log('  - Baggage Type:', typeof flight.baggage);
        console.log('  - Baggage Details:', JSON.stringify(flight.baggage, null, 2));
      }
      console.log('');
      console.log('💺 SEAT AVAILABILITY (From API):');
      console.log('  - Number of Bookable Seats:', flight.numberOfBookableSeats || '❌ NOT FROM API');
      console.log('  - Available Passengers:', flight.available_passengers || 'N/A');
      console.log('');
      console.log('📦 ADDITIONAL OPTIONS (Only shown if API data available):');
      console.log('  - Extra Baggage Option:');
      if (flight.baggage) {
        console.log('    • Status: ✅ WILL BE SHOWN (API data available)');
        console.log('    • Current Baggage (API):', flight.baggage);
        console.log('    • Add-on Price: ₦15,000 (Platform service)');
      } else {
        console.log('    • Status: ❌ HIDDEN (No API data)');
      }
      console.log('  - Seat Selection Option:');
      if (flight.numberOfBookableSeats) {
        console.log('    • Status: ✅ WILL BE SHOWN (API data available)');
        console.log('    • Available Seats (API):', flight.numberOfBookableSeats);
      } else {
        console.log('    • Status: ❌ HIDDEN (No API data)');
      }
      console.log('🔍 RAW API DATA:');
      if (flight.rawOffer) {
        console.log('  - Raw Offer Available: ✅');
        console.log('  - Raw Offer Keys:', Object.keys(flight.rawOffer));
        console.log('  - Raw Offer Sample:', {
          id: flight.rawOffer.id,
          total_amount: flight.rawOffer.total_amount,
          total_currency: flight.rawOffer.total_currency,
          base_amount: flight.rawOffer.base_amount,
          tax_amount: flight.rawOffer.tax_amount,
          cabin_class: flight.rawOffer.cabin_class,
          available_passengers: flight.rawOffer.available_passengers,
          slices: flight.rawOffer.slices?.length || 0,
          conditions: flight.rawOffer.conditions ? 'Available' : 'Not available'
        });
      } else {
        console.log('  - Raw Offer: ❌ NOT AVAILABLE');
      }
      console.log('');
      console.log('❌ DUMMY/HARDCODED DATA:');
      if (!flight.baggage) console.log('  ⚠️ Baggage info is using fallback');
      if (!flight.numberOfBookableSeats) console.log('  ⚠️ Seat availability not from API');
      if (!flight.price?.base && !flight.baseFare) console.log('  ⚠️ Base fare not extracted from API');
      if (!flight.price?.taxes && !flight.taxes) console.log('  ⚠️ Taxes not extracted from API');
      console.log('');
      console.log('💡 SUMMARY:');
      console.log('  - Core flight data: ✅ From API');
      console.log('  - Pricing breakdown: ' + (flight.price?.base && flight.price?.taxes ? '✅ From API' : '⚠️ Partially from API'));
      console.log('  - Baggage info: ' + (flight.baggage ? '✅ From API' : '❌ Not available'));
      console.log('  - Seat availability: ' + (flight.numberOfBookableSeats ? '✅ From API' : '❌ Not available'));
      console.log('  - Additional options prices: Platform services (calculated from API data)');
      console.log('========================================');
    } else {
      console.error('❌ No flight data found. Redirecting to flight search...');
      navigate('/results', { 
        replace: true,
        state: { error: 'Please select a flight first' }
      });
    }
  }, [flight, navigate]);

  const [passengerData, setPassengerData] = useState(preservedPassengerData || {
    passengerType: 'Adult',
    title: 'Mr',
    fullName: '',
    dateOfBirth: '',
    gender: '',
    nationality: 'Nigeria',
    nin: '',
    passportNumber: '',
    email: '',
    phoneNumber: '',
    phoneCountryCode: '+234',
    nextOfKin: '',
    emergencyContact: '',
    emergencyCountryCode: '+234'
  });

  const [additionalOptions, setAdditionalOptions] = useState(preservedAdditionalOptions || {
    extraBaggage: false,
    seatSelection: false
  });
  const [acceptedTerms, setAcceptedTerms] = useState(preservedAcceptedTerms || false);
  const [currency, setCurrency] = useState(null); // Currency based on location

  // Initialize currency on component mount
  useEffect(() => {
    const initCurrency = async () => {
      try {
        console.log('PassengerInfoPage: Initializing currency...');
        const detectedCurrency = await initializeCurrency(true);
        setCurrency(detectedCurrency);
        console.log('PassengerInfoPage: Currency initialized:', {
          code: detectedCurrency.code,
          symbol: detectedCurrency.symbol,
          name: detectedCurrency.name
        });
      } catch (error) {
        console.error('PassengerInfoPage: Error initializing currency:', error);
        const fallbackCurrency = getCurrency();
        setCurrency(fallbackCurrency);
      }
    };
    initCurrency();

    // Listen for currency initialization from App
    const handleCurrencyInit = (event) => {
      console.log('PassengerInfoPage: Received currency from App:', event.detail);
      setCurrency(event.detail);
    };
    
    window.addEventListener('currencyInitialized', handleCurrencyInit);
    return () => {
      window.removeEventListener('currencyInitialized', handleCurrencyInit);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPassengerData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOptionChange = (option) => {
    setAdditionalOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      alert('Please accept the terms and conditions to continue.');
      return;
    }
    proceedToPayment();
  };

  const buildPaymentState = () => {
    const p = flight.price;
    return {
      flight: {
        ...flight,
        departureCity: flight.departureCity || (flight.departureAirport === 'LOS' ? 'Lagos' : 
                        flight.departureAirport === 'ABV' ? 'Abuja' : 
                        flight.departureAirport === 'LHR' ? 'London' : 
                        flight.departureAirport === 'DXB' ? 'Dubai' : 
                        flight.departureAirport === 'ACC' ? 'Accra' : 'City'),
        arrivalCity: flight.arrivalCity || (flight.arrivalAirport === 'LOS' ? 'Lagos' : 
                       flight.arrivalAirport === 'ABV' ? 'Abuja' : 
                       flight.arrivalAirport === 'LHR' ? 'London' : 
                       flight.arrivalAirport === 'DXB' ? 'Dubai' : 
                       flight.arrivalAirport === 'ACC' ? 'Accra' : 'City'),
        travelClass: flight.travelClass || 'Business',
        // Keep Duffel price object (base/taxes/total/currency). Do NOT set price to flight.total —
        // that string is a formatted display value and would wipe out breakdown fields.
        baseFare: (typeof flight.baseFare === 'string' && flight.baseFare.trim() !== '')
          ? flight.baseFare
          : (p?.base != null && p !== null ? formatFlightPriceComponent(p.base, p) : null),
        taxes: (typeof flight.taxes === 'string' && flight.taxes.trim() !== '')
          ? flight.taxes
          : (p?.taxes != null && p !== null ? formatFlightPriceComponent(p.taxes, p) : null),
        priceObject: p || null,
        total: flight.total || null,
        isRoundTrip: flight.isRoundTrip,
        returnFlight: flight.returnFlight,
        outbound: flight.outbound,
        return: flight.return
      },
      passengerData,
      additionalOptions
    };
  };

  const proceedToPayment = () => {
    // Require login before proceeding to payment
    const isSignedIn = localStorage.getItem('isSignedIn') === 'true';
    const userData = localStorage.getItem('user');

    const paymentState = buildPaymentState();

    if (!isSignedIn || !userData) {
      // Redirect to login first, then back to payment with full state
      navigate('/login', {
        state: {
          redirectTo: '/payment',
          redirectState: paymentState
        }
      });
      return;
    }

    // User is logged in – go straight to payment
    navigate('/payment', { 
      state: paymentState
    });
  };



  return (
    <div className="passenger-info-page">
      <Header />
      
      {/* Flight Summary Card */}
      <section className="passenger-summary-section">
        <div className="page-container">
          <div className="passenger-summary-card">
            <div className="summary-airline">
              <span className="summary-logo">{flight.airlineLogo}</span>
              <div>
                <h3 className="summary-airline-name">{flight.airline}</h3>
                <p className="summary-aircraft">{flight.aircraftType}</p>
              </div>
            </div>
            
            {/* Multi-City Routes */}
            {flight.isMultiCity && flight.slices && flight.slices.length > 0 && (
              <>
                {flight.slices.map((slice, sliceIndex) => {
                  const firstSegment = slice.segments?.[0];
                  const lastSegment = slice.segments?.[slice.segments?.length - 1];
                  
                  const formatTime = (isoString) => {
                    if (!isoString) return '';
                    try {
                      const date = new Date(isoString);
                      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                    } catch {
                      return '';
                    }
                  };
                  
                  const getCityName = (airportCode) => {
                    const cityMap = {
                      'LOS': 'Lagos', 'ABV': 'Abuja', 'LHR': 'London', 'DXB': 'Dubai',
                      'ACC': 'Accra', 'CDG': 'Paris', 'JFK': 'New York', 'LAX': 'Los Angeles',
                      'NBO': 'Nairobi', 'CPT': 'Cape Town', 'CAI': 'Cairo', 'ADD': 'Addis Ababa'
                    };
                    return cityMap[airportCode] || 'City';
                  };
                  
                  if (!firstSegment || !lastSegment) return null;
                  
                  const originCode = firstSegment.origin?.iata_code || '';
                  const destCode = lastSegment.destination?.iata_code || '';
                  const stops = slice.segments ? (slice.segments.length - 1) : 0;
                  
                  return (
                    <div key={sliceIndex} className="summary-route" style={{ marginBottom: sliceIndex < flight.slices.length - 1 ? '15px' : '0' }}>
                      <div className="summary-route-label">Route {sliceIndex + 1} of {flight.slices.length}</div>
                      <div className="summary-route-segment">
                        <span className="summary-airport">{originCode}</span>
                        <span className="summary-city">{firstSegment.origin?.city_name || getCityName(originCode)}</span>
                        <span className="summary-time">{formatTime(firstSegment.departing_at)}</span>
                      </div>
                      
                      <div className="summary-route-connector">
                        <span className="summary-duration">{slice.duration || 'N/A'}</span>
                        <span className="summary-stops">{stops === 0 ? 'Non-stop' : `${stops} Stop${stops > 1 ? 's' : ''}`}</span>
                      </div>
                      
                      <div className="summary-route-segment">
                        <span className="summary-airport">{destCode}</span>
                        <span className="summary-city">{lastSegment.destination?.city_name || getCityName(destCode)}</span>
                        <span className="summary-time">{formatTime(lastSegment.arriving_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Outbound Flight (One-way or Round-trip) */}
            {!flight.isMultiCity && (
              <div className="summary-route">
                <div className="summary-route-label">Outbound</div>
                <div className="summary-route-segment">
                  <span className="summary-airport">{flight.departureAirport}</span>
                  <span className="summary-city">{flight.departureCity || 'Lagos'}</span>
                  <span className="summary-time">{flight.departureTime}</span>
                </div>
                
                <div className="summary-route-connector">
                  <span className="summary-duration">{flight.duration}</span>
                  <span className="summary-stops">{flight.stops}</span>
                </div>
                
                <div className="summary-route-segment">
                  <span className="summary-airport">{flight.arrivalAirport}</span>
                  <span className="summary-city">{flight.arrivalCity || 'London'}</span>
                  <span className="summary-time">{flight.arrivalTime}</span>
                </div>
              </div>
            )}

            {/* Return Flight (if round-trip, NOT multi-city) */}
            {flight.isRoundTrip && !flight.isMultiCity && flight.returnFlight && (
              <div className="summary-route return-route">
                <div className="summary-route-label return-label">Return</div>
                <div className="summary-route-segment">
                  <span className="summary-airport">{flight.returnFlight.departureAirport}</span>
                  <span className="summary-city">
                    {flight.returnFlight.departureAirport === 'LOS' ? 'Lagos' : 
                     flight.returnFlight.departureAirport === 'ABV' ? 'Abuja' : 
                     flight.returnFlight.departureAirport === 'LHR' ? 'London' : 
                     flight.returnFlight.departureAirport === 'DXB' ? 'Dubai' : 
                     flight.returnFlight.departureAirport === 'ACC' ? 'Accra' : 
                     flight.returnFlight.departureAirport === 'CDG' ? 'Paris' : 'City'}
                  </span>
                  <span className="summary-time">{flight.returnFlight.departureTime}</span>
                </div>
                
                <div className="summary-route-connector">
                  <span className="summary-duration">{flight.returnFlight.duration}</span>
                  <span className="summary-stops">
                    {flight.returnFlight.stops === 0 ? 'Non-stop' : `${flight.returnFlight.stops} Stop${flight.returnFlight.stops > 1 ? 's' : ''}`}
                  </span>
                </div>
                
                <div className="summary-route-segment">
                  <span className="summary-airport">{flight.returnFlight.arrivalAirport}</span>
                  <span className="summary-city">
                    {flight.returnFlight.arrivalAirport === 'LOS' ? 'Lagos' : 
                     flight.returnFlight.arrivalAirport === 'ABV' ? 'Abuja' : 
                     flight.returnFlight.arrivalAirport === 'LHR' ? 'London' : 
                     flight.returnFlight.arrivalAirport === 'DXB' ? 'Dubai' : 
                     flight.returnFlight.arrivalAirport === 'ACC' ? 'Accra' : 
                     flight.returnFlight.arrivalAirport === 'CDG' ? 'Paris' : 'City'}
                  </span>
                  <span className="summary-time">{flight.returnFlight.arrivalTime}</span>
                </div>
              </div>
            )}
            
            <div className="summary-footer">
              <span className="summary-class">{(() => {
                // CRITICAL: Prioritize travelClass (set from details page) over cabinClass
                // The travelClass should be the one set when user clicks "Book Now"
                const rawClass = flight?.travelClass || flight?.cabinClass || 'Business';
                const normalizedClass = typeof rawClass === 'string' 
                  ? rawClass.toLowerCase().trim().replace(/\s+/g, '_')
                  : 'business';
                console.log('🔵 Passenger Info - Displaying class:', {
                  flightObject: flight,
                  rawTravelClass: flight?.travelClass,
                  rawCabinClass: flight?.cabinClass,
                  rawClass: rawClass,
                  normalizedClass: normalizedClass
                });
                const classMap = {
                  'economy': 'Economy',
                  'business': 'Business',
                  'premium_economy': 'Premium Economy',
                  'premiumeconomy': 'Premium Economy',
                  'first': 'First Class',
                  'firstclass': 'First Class'
                };
                const displayClass = classMap[normalizedClass] || (typeof rawClass === 'string' 
                  ? rawClass.charAt(0).toUpperCase() + rawClass.slice(1)
                  : 'Business');
                console.log('🔵 Final display class:', displayClass);
                return displayClass;
              })()}</span>
              <span className="summary-price">
                {(() => {
                  if (!currency) return 'Loading...';
                  // If we already have a formatted total (from results/details), use it directly
                  if (typeof flight?.total === 'string' && flight.total.trim() !== '') {
                    return flight.total;
                  }
                  if (typeof flight?.priceFormatted === 'string' && flight.priceFormatted.trim() !== '') {
                    return flight.priceFormatted;
                  }
                  // Otherwise extract numeric total from API price object and format once
                  let priceValue = 0;
                  if (typeof flight?.total === 'number') {
                    priceValue = flight.total;
                  } else if (typeof flight?.price === 'number') {
                    priceValue = flight.price;
                  } else if (flight?.price?.total) {
                    priceValue = typeof flight.price.total === 'number' ? flight.price.total : parseFloat(flight.price.total);
                  } else if (flight?.priceFormatted) {
                    const match = flight.priceFormatted.match(/[\d,]+\.?\d*/);
                    if (match) {
                      priceValue = parseFloat(match[0].replace(/,/g, ''));
                    }
                  }
                  return formatPrice(priceValue);
                })()}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="passenger-form-section">
        <div className="page-container">
          <div className="form-layout">
            {/* Passenger Details Form */}
            <main className="passenger-form-main">
              <div className="form-card">
                <div className="form-card-header">
                  <h2 className="form-title">Passenger Information</h2>
                  <p className="form-subtitle">Please provide the details for all passengers</p>
                </div>

                <form onSubmit={handleSubmit} className="passenger-form">
                  {/* Passenger Type */}
                  <div className="form-group">
                    <label htmlFor="passengerType" className="form-label">
                      Passenger Type <span className="required">*</span>
                    </label>
                    <select
                      id="passengerType"
                      name="passengerType"
                      value={passengerData.passengerType}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="Adult">Adult</option>
                      <option value="Child">Child (2-11 years)</option>
                      <option value="Infant">Infant (Under 2 years)</option>
                    </select>
                  </div>

                  {/* Title */}
                  <div className="form-group">
                    <label htmlFor="title" className="form-label">
                      Title <span className="required">*</span>
                    </label>
                    <select
                      id="title"
                      name="title"
                      value={passengerData.title}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Ms">Ms</option>
                      <option value="Miss">Miss</option>
                    </select>
                  </div>

                  {/* Full Name */}
                  <div className="form-group">
                    <label htmlFor="fullName" className="form-label">
                      Full Name (as on ID) <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={passengerData.fullName}
                      onChange={handleInputChange}
                      placeholder="Enter full name"
                      className="form-input"
                      required
                    />
                  </div>

                  {/* Date of Birth */}
                  <div className="form-group">
                    <label htmlFor="dateOfBirth" className="form-label">
                      Date of Birth <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={passengerData.dateOfBirth}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      max={new Date().toISOString().split('T')[0]} // Prevent future dates
                    />
                  </div>

                  {/* Gender */}
                  <div className="form-group">
                    <label htmlFor="gender" className="form-label">
                      Gender <span className="required">*</span>
                    </label>
                    <select
                      id="gender"
                      name="gender"
                      value={passengerData.gender}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Nationality */}
                  <div className="form-group">
                    <label htmlFor="nationality" className="form-label">
                      Nationality <span className="required">*</span>
                    </label>
                    <select
                      id="nationality"
                      name="nationality"
                      value={passengerData.nationality}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select Nationality</option>
                      <option value="Afghanistan">Afghanistan</option>
                      <option value="Albania">Albania</option>
                      <option value="Algeria">Algeria</option>
                      <option value="Andorra">Andorra</option>
                      <option value="Angola">Angola</option>
                      <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                      <option value="Argentina">Argentina</option>
                      <option value="Armenia">Armenia</option>
                      <option value="Australia">Australia</option>
                      <option value="Austria">Austria</option>
                      <option value="Azerbaijan">Azerbaijan</option>
                      <option value="Bahamas">Bahamas</option>
                      <option value="Bahrain">Bahrain</option>
                      <option value="Bangladesh">Bangladesh</option>
                      <option value="Barbados">Barbados</option>
                      <option value="Belarus">Belarus</option>
                      <option value="Belgium">Belgium</option>
                      <option value="Belize">Belize</option>
                      <option value="Benin">Benin</option>
                      <option value="Bhutan">Bhutan</option>
                      <option value="Bolivia">Bolivia</option>
                      <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                      <option value="Botswana">Botswana</option>
                      <option value="Brazil">Brazil</option>
                      <option value="Brunei">Brunei</option>
                      <option value="Bulgaria">Bulgaria</option>
                      <option value="Burkina Faso">Burkina Faso</option>
                      <option value="Burundi">Burundi</option>
                      <option value="Cambodia">Cambodia</option>
                      <option value="Cameroon">Cameroon</option>
                      <option value="Canada">Canada</option>
                      <option value="Cape Verde">Cape Verde</option>
                      <option value="Central African Republic">Central African Republic</option>
                      <option value="Chad">Chad</option>
                      <option value="Chile">Chile</option>
                      <option value="China">China</option>
                      <option value="Colombia">Colombia</option>
                      <option value="Comoros">Comoros</option>
                      <option value="Congo">Congo</option>
                      <option value="Costa Rica">Costa Rica</option>
                      <option value="Croatia">Croatia</option>
                      <option value="Cuba">Cuba</option>
                      <option value="Cyprus">Cyprus</option>
                      <option value="Czech Republic">Czech Republic</option>
                      <option value="Denmark">Denmark</option>
                      <option value="Djibouti">Djibouti</option>
                      <option value="Dominica">Dominica</option>
                      <option value="Dominican Republic">Dominican Republic</option>
                      <option value="East Timor">East Timor</option>
                      <option value="Ecuador">Ecuador</option>
                      <option value="Egypt">Egypt</option>
                      <option value="El Salvador">El Salvador</option>
                      <option value="Equatorial Guinea">Equatorial Guinea</option>
                      <option value="Eritrea">Eritrea</option>
                      <option value="Estonia">Estonia</option>
                      <option value="Eswatini">Eswatini</option>
                      <option value="Ethiopia">Ethiopia</option>
                      <option value="Fiji">Fiji</option>
                      <option value="Finland">Finland</option>
                      <option value="France">France</option>
                      <option value="Gabon">Gabon</option>
                      <option value="Gambia">Gambia</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Germany">Germany</option>
                      <option value="Ghana">Ghana</option>
                      <option value="Greece">Greece</option>
                      <option value="Grenada">Grenada</option>
                      <option value="Guatemala">Guatemala</option>
                      <option value="Guinea">Guinea</option>
                      <option value="Guinea-Bissau">Guinea-Bissau</option>
                      <option value="Guyana">Guyana</option>
                      <option value="Haiti">Haiti</option>
                      <option value="Honduras">Honduras</option>
                      <option value="Hungary">Hungary</option>
                      <option value="Iceland">Iceland</option>
                      <option value="India">India</option>
                      <option value="Indonesia">Indonesia</option>
                      <option value="Iran">Iran</option>
                      <option value="Iraq">Iraq</option>
                      <option value="Ireland">Ireland</option>
                      <option value="Israel">Israel</option>
                      <option value="Italy">Italy</option>
                      <option value="Ivory Coast">Ivory Coast</option>
                      <option value="Jamaica">Jamaica</option>
                      <option value="Japan">Japan</option>
                      <option value="Jordan">Jordan</option>
                      <option value="Kazakhstan">Kazakhstan</option>
                      <option value="Kenya">Kenya</option>
                      <option value="Kiribati">Kiribati</option>
                      <option value="Kosovo">Kosovo</option>
                      <option value="Kuwait">Kuwait</option>
                      <option value="Kyrgyzstan">Kyrgyzstan</option>
                      <option value="Laos">Laos</option>
                      <option value="Latvia">Latvia</option>
                      <option value="Lebanon">Lebanon</option>
                      <option value="Lesotho">Lesotho</option>
                      <option value="Liberia">Liberia</option>
                      <option value="Libya">Libya</option>
                      <option value="Liechtenstein">Liechtenstein</option>
                      <option value="Lithuania">Lithuania</option>
                      <option value="Luxembourg">Luxembourg</option>
                      <option value="Madagascar">Madagascar</option>
                      <option value="Malawi">Malawi</option>
                      <option value="Malaysia">Malaysia</option>
                      <option value="Maldives">Maldives</option>
                      <option value="Mali">Mali</option>
                      <option value="Malta">Malta</option>
                      <option value="Marshall Islands">Marshall Islands</option>
                      <option value="Mauritania">Mauritania</option>
                      <option value="Mauritius">Mauritius</option>
                      <option value="Mexico">Mexico</option>
                      <option value="Micronesia">Micronesia</option>
                      <option value="Moldova">Moldova</option>
                      <option value="Monaco">Monaco</option>
                      <option value="Mongolia">Mongolia</option>
                      <option value="Montenegro">Montenegro</option>
                      <option value="Morocco">Morocco</option>
                      <option value="Mozambique">Mozambique</option>
                      <option value="Myanmar">Myanmar</option>
                      <option value="Namibia">Namibia</option>
                      <option value="Nauru">Nauru</option>
                      <option value="Nepal">Nepal</option>
                      <option value="Netherlands">Netherlands</option>
                      <option value="New Zealand">New Zealand</option>
                      <option value="Nicaragua">Nicaragua</option>
                      <option value="Niger">Niger</option>
                      <option value="Nigeria">Nigeria</option>
                      <option value="North Korea">North Korea</option>
                      <option value="North Macedonia">North Macedonia</option>
                      <option value="Norway">Norway</option>
                      <option value="Oman">Oman</option>
                      <option value="Pakistan">Pakistan</option>
                      <option value="Palau">Palau</option>
                      <option value="Palestine">Palestine</option>
                      <option value="Panama">Panama</option>
                      <option value="Papua New Guinea">Papua New Guinea</option>
                      <option value="Paraguay">Paraguay</option>
                      <option value="Peru">Peru</option>
                      <option value="Philippines">Philippines</option>
                      <option value="Poland">Poland</option>
                      <option value="Portugal">Portugal</option>
                      <option value="Qatar">Qatar</option>
                      <option value="Romania">Romania</option>
                      <option value="Russia">Russia</option>
                      <option value="Rwanda">Rwanda</option>
                      <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                      <option value="Saint Lucia">Saint Lucia</option>
                      <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                      <option value="Samoa">Samoa</option>
                      <option value="San Marino">San Marino</option>
                      <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                      <option value="Saudi Arabia">Saudi Arabia</option>
                      <option value="Senegal">Senegal</option>
                      <option value="Serbia">Serbia</option>
                      <option value="Seychelles">Seychelles</option>
                      <option value="Sierra Leone">Sierra Leone</option>
                      <option value="Singapore">Singapore</option>
                      <option value="Slovakia">Slovakia</option>
                      <option value="Slovenia">Slovenia</option>
                      <option value="Solomon Islands">Solomon Islands</option>
                      <option value="Somalia">Somalia</option>
                      <option value="South Africa">South Africa</option>
                      <option value="South Korea">South Korea</option>
                      <option value="South Sudan">South Sudan</option>
                      <option value="Spain">Spain</option>
                      <option value="Sri Lanka">Sri Lanka</option>
                      <option value="Sudan">Sudan</option>
                      <option value="Suriname">Suriname</option>
                      <option value="Sweden">Sweden</option>
                      <option value="Switzerland">Switzerland</option>
                      <option value="Syria">Syria</option>
                      <option value="Taiwan">Taiwan</option>
                      <option value="Tajikistan">Tajikistan</option>
                      <option value="Tanzania">Tanzania</option>
                      <option value="Thailand">Thailand</option>
                      <option value="Togo">Togo</option>
                      <option value="Tonga">Tonga</option>
                      <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                      <option value="Tunisia">Tunisia</option>
                      <option value="Turkey">Turkey</option>
                      <option value="Turkmenistan">Turkmenistan</option>
                      <option value="Tuvalu">Tuvalu</option>
                      <option value="Uganda">Uganda</option>
                      <option value="Ukraine">Ukraine</option>
                      <option value="United Arab Emirates">United Arab Emirates</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="United States">United States</option>
                      <option value="Uruguay">Uruguay</option>
                      <option value="Uzbekistan">Uzbekistan</option>
                      <option value="Vanuatu">Vanuatu</option>
                      <option value="Vatican City">Vatican City</option>
                      <option value="Venezuela">Venezuela</option>
                      <option value="Vietnam">Vietnam</option>
                      <option value="Yemen">Yemen</option>
                      <option value="Zambia">Zambia</option>
                      <option value="Zimbabwe">Zimbabwe</option>
                    </select>
                  </div>

                  {/* NIN (for domestic flights) */}
                  {flight.departureAirport === 'LOS' && (flight.arrivalAirport === 'ABV' || flight.arrivalAirport === 'ACC') && (
                    <div className="form-group">
                      <label htmlFor="nin" className="form-label">
                        NIN (National Identification Number)
                      </label>
                      <input
                        type="text"
                        id="nin"
                        name="nin"
                        value={passengerData.nin}
                        onChange={handleInputChange}
                        placeholder="Enter NIN (optional)"
                        className="form-input"
                      />
                    </div>
                  )}

                  {/* Passport Number (for international flights) */}
                  {flight.arrivalAirport !== 'LOS' && flight.arrivalAirport !== 'ABV' && flight.arrivalAirport !== 'ACC' && (
                    <div className="form-group">
                      <label htmlFor="passportNumber" className="form-label">
                        Passport Number
                      </label>
                      <input
                        type="text"
                        id="passportNumber"
                        name="passportNumber"
                        value={passengerData.passportNumber}
                        onChange={handleInputChange}
                        placeholder="Enter passport number (optional)"
                        className="form-input"
                      />
                    </div>
                  )}

                  {/* Contact Information Section */}
                  <div className="form-section-divider">
                    <h3 className="form-section-title">Contact Information</h3>
                  </div>

                  {/* Email */}
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={passengerData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      className="form-input"
                      required
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="form-group">
                    <label htmlFor="phoneNumber" className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <div className="phone-input-wrapper">
                      <select
                        id="phoneCountryCode"
                        name="phoneCountryCode"
                        value={passengerData.phoneCountryCode}
                        onChange={handleInputChange}
                        className="phone-country-code-select"
                      >
                        <option value="+1">🇺🇸 +1 (US/CA)</option>
                        <option value="+44">🇬🇧 +44 (UK)</option>
                        <option value="+234">🇳🇬 +234 (Nigeria)</option>
                        <option value="+233">🇬🇭 +233 (Ghana)</option>
                        <option value="+254">🇰🇪 +254 (Kenya)</option>
                        <option value="+27">🇿🇦 +27 (South Africa)</option>
                        <option value="+971">🇦🇪 +971 (UAE)</option>
                        <option value="+33">🇫🇷 +33 (France)</option>
                        <option value="+49">🇩🇪 +49 (Germany)</option>
                        <option value="+39">🇮🇹 +39 (Italy)</option>
                        <option value="+34">🇪🇸 +34 (Spain)</option>
                        <option value="+7">🇷🇺 +7 (Russia)</option>
                        <option value="+81">🇯🇵 +81 (Japan)</option>
                        <option value="+86">🇨🇳 +86 (China)</option>
                        <option value="+91">🇮🇳 +91 (India)</option>
                        <option value="+61">🇦🇺 +61 (Australia)</option>
                        <option value="+55">🇧🇷 +55 (Brazil)</option>
                        <option value="+52">🇲🇽 +52 (Mexico)</option>
                        <option value="+20">🇪🇬 +20 (Egypt)</option>
                        <option value="+251">🇪🇹 +251 (Ethiopia)</option>
                        <option value="+255">🇹🇿 +255 (Tanzania)</option>
                        <option value="+256">🇺🇬 +256 (Uganda)</option>
                        <option value="+250">🇷🇼 +250 (Rwanda)</option>
                        <option value="+225">🇨🇮 +225 (Ivory Coast)</option>
                        <option value="+221">🇸🇳 +221 (Senegal)</option>
                        <option value="+212">🇲🇦 +212 (Morocco)</option>
                        <option value="+213">🇩🇿 +213 (Algeria)</option>
                        <option value="+216">🇹🇳 +216 (Tunisia)</option>
                        <option value="+220">🇬🇲 +220 (Gambia)</option>
                        <option value="+232">🇸🇱 +232 (Sierra Leone)</option>
                        <option value="+229">🇧🇯 +229 (Benin)</option>
                        <option value="+226">🇧🇫 +226 (Burkina Faso)</option>
                        <option value="+227">🇳🇪 +227 (Niger)</option>
                        <option value="+228">🇹🇬 +228 (Togo)</option>
                        <option value="+235">🇹🇩 +235 (Chad)</option>
                        <option value="+236">🇨🇫 +236 (Central African Republic)</option>
                        <option value="+237">🇨🇲 +237 (Cameroon)</option>
                        <option value="+240">🇬🇶 +240 (Equatorial Guinea)</option>
                        <option value="+241">🇬🇦 +241 (Gabon)</option>
                        <option value="+242">🇨🇬 +242 (Congo)</option>
                        <option value="+243">🇨🇩 +243 (DR Congo)</option>
                        <option value="+244">🇦🇴 +244 (Angola)</option>
                        <option value="+245">🇬🇼 +245 (Guinea-Bissau)</option>
                        <option value="+246">🇮🇴 +246 (British Indian Ocean Territory)</option>
                        <option value="+248">🇸🇨 +248 (Seychelles)</option>
                        <option value="+249">🇸🇩 +249 (Sudan)</option>
                        <option value="+252">🇸🇴 +252 (Somalia)</option>
                        <option value="+253">🇩🇯 +253 (Djibouti)</option>
                        <option value="+257">🇧🇮 +257 (Burundi)</option>
                        <option value="+258">🇲🇿 +258 (Mozambique)</option>
                        <option value="+260">🇿🇲 +260 (Zambia)</option>
                        <option value="+261">🇲🇬 +261 (Madagascar)</option>
                        <option value="+262">🇷🇪 +262 (Réunion)</option>
                        <option value="+263">🇿🇼 +263 (Zimbabwe)</option>
                        <option value="+264">🇳🇦 +264 (Namibia)</option>
                        <option value="+265">🇲🇼 +265 (Malawi)</option>
                        <option value="+266">🇱🇸 +266 (Lesotho)</option>
                        <option value="+267">🇧🇼 +267 (Botswana)</option>
                        <option value="+268">🇸🇿 +268 (Eswatini)</option>
                        <option value="+269">🇰🇲 +269 (Comoros)</option>
                        <option value="+290">🇸🇭 +290 (Saint Helena)</option>
                        <option value="+291">🇪🇷 +291 (Eritrea)</option>
                        <option value="+297">🇦🇼 +297 (Aruba)</option>
                        <option value="+298">🇫🇴 +298 (Faroe Islands)</option>
                        <option value="+299">🇬🇱 +299 (Greenland)</option>
                        <option value="+350">🇬🇮 +350 (Gibraltar)</option>
                        <option value="+351">🇵🇹 +351 (Portugal)</option>
                        <option value="+352">🇱🇺 +352 (Luxembourg)</option>
                        <option value="+353">🇮🇪 +353 (Ireland)</option>
                        <option value="+354">🇮🇸 +354 (Iceland)</option>
                        <option value="+355">🇦🇱 +355 (Albania)</option>
                        <option value="+356">🇲🇹 +356 (Malta)</option>
                        <option value="+357">🇨🇾 +357 (Cyprus)</option>
                        <option value="+358">🇫🇮 +358 (Finland)</option>
                        <option value="+359">🇧🇬 +359 (Bulgaria)</option>
                        <option value="+370">🇱🇹 +370 (Lithuania)</option>
                        <option value="+371">🇱🇻 +371 (Latvia)</option>
                        <option value="+372">🇪🇪 +372 (Estonia)</option>
                        <option value="+373">🇲🇩 +373 (Moldova)</option>
                        <option value="+374">🇦🇲 +374 (Armenia)</option>
                        <option value="+375">🇧🇾 +375 (Belarus)</option>
                        <option value="+376">🇦🇩 +376 (Andorra)</option>
                        <option value="+377">🇲🇨 +377 (Monaco)</option>
                        <option value="+378">🇸🇲 +378 (San Marino)</option>
                        <option value="+380">🇺🇦 +380 (Ukraine)</option>
                        <option value="+381">🇷🇸 +381 (Serbia)</option>
                        <option value="+382">🇲🇪 +382 (Montenegro)</option>
                        <option value="+383">🇽🇰 +383 (Kosovo)</option>
                        <option value="+385">🇭🇷 +385 (Croatia)</option>
                        <option value="+386">🇸🇮 +386 (Slovenia)</option>
                        <option value="+387">🇧🇦 +387 (Bosnia and Herzegovina)</option>
                        <option value="+389">🇲🇰 +389 (North Macedonia)</option>
                        <option value="+420">🇨🇿 +420 (Czech Republic)</option>
                        <option value="+421">🇸🇰 +421 (Slovakia)</option>
                        <option value="+423">🇱🇮 +423 (Liechtenstein)</option>
                        <option value="+500">🇫🇰 +500 (Falkland Islands)</option>
                        <option value="+501">🇧🇿 +501 (Belize)</option>
                        <option value="+502">🇬🇹 +502 (Guatemala)</option>
                        <option value="+503">🇸🇻 +503 (El Salvador)</option>
                        <option value="+504">🇭🇳 +504 (Honduras)</option>
                        <option value="+505">🇳🇮 +505 (Nicaragua)</option>
                        <option value="+506">🇨🇷 +506 (Costa Rica)</option>
                        <option value="+507">🇵🇦 +507 (Panama)</option>
                        <option value="+508">🇵🇲 +508 (Saint Pierre and Miquelon)</option>
                        <option value="+509">🇭🇹 +509 (Haiti)</option>
                        <option value="+590">🇬🇵 +590 (Guadeloupe)</option>
                        <option value="+591">🇧🇴 +591 (Bolivia)</option>
                        <option value="+592">🇬🇾 +592 (Guyana)</option>
                        <option value="+593">🇪🇨 +593 (Ecuador)</option>
                        <option value="+594">🇬🇫 +594 (French Guiana)</option>
                        <option value="+595">🇵🇾 +595 (Paraguay)</option>
                        <option value="+596">🇲🇶 +596 (Martinique)</option>
                        <option value="+597">🇸🇷 +597 (Suriname)</option>
                        <option value="+598">🇺🇾 +598 (Uruguay)</option>
                        <option value="+599">🇧🇶 +599 (Caribbean Netherlands)</option>
                        <option value="+670">🇹🇱 +670 (East Timor)</option>
                        <option value="+672">🇦🇶 +672 (Antarctica)</option>
                        <option value="+673">🇧🇳 +673 (Brunei)</option>
                        <option value="+674">🇳🇷 +674 (Nauru)</option>
                        <option value="+675">🇵🇬 +675 (Papua New Guinea)</option>
                        <option value="+676">🇹🇴 +676 (Tonga)</option>
                        <option value="+677">🇸🇧 +677 (Solomon Islands)</option>
                        <option value="+678">🇻🇺 +678 (Vanuatu)</option>
                        <option value="+679">🇫🇯 +679 (Fiji)</option>
                        <option value="+680">🇵🇼 +680 (Palau)</option>
                        <option value="+681">🇼🇫 +681 (Wallis and Futuna)</option>
                        <option value="+682">🇨🇰 +682 (Cook Islands)</option>
                        <option value="+683">🇳🇺 +683 (Niue)</option>
                        <option value="+685">🇼🇸 +685 (Samoa)</option>
                        <option value="+686">🇰🇮 +686 (Kiribati)</option>
                        <option value="+687">🇳🇨 +687 (New Caledonia)</option>
                        <option value="+688">🇹🇻 +688 (Tuvalu)</option>
                        <option value="+689">🇵🇫 +689 (French Polynesia)</option>
                        <option value="+690">🇹🇰 +690 (Tokelau)</option>
                        <option value="+691">🇫🇲 +691 (Micronesia)</option>
                        <option value="+692">🇲🇭 +692 (Marshall Islands)</option>
                        <option value="+850">🇰🇵 +850 (North Korea)</option>
                        <option value="+852">🇭🇰 +852 (Hong Kong)</option>
                        <option value="+853">🇲🇴 +853 (Macau)</option>
                        <option value="+855">🇰🇭 +855 (Cambodia)</option>
                        <option value="+856">🇱🇦 +856 (Laos)</option>
                        <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                        <option value="+886">🇹🇼 +886 (Taiwan)</option>
                        <option value="+960">🇲🇻 +960 (Maldives)</option>
                        <option value="+961">🇱🇧 +961 (Lebanon)</option>
                        <option value="+962">🇯🇴 +962 (Jordan)</option>
                        <option value="+963">🇸🇾 +963 (Syria)</option>
                        <option value="+964">🇮🇶 +964 (Iraq)</option>
                        <option value="+965">🇰🇼 +965 (Kuwait)</option>
                        <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                        <option value="+967">🇾🇪 +967 (Yemen)</option>
                        <option value="+968">🇴🇲 +968 (Oman)</option>
                        <option value="+970">🇵🇸 +970 (Palestine)</option>
                        <option value="+971">🇦🇪 +971 (UAE)</option>
                        <option value="+972">🇮🇱 +972 (Israel)</option>
                        <option value="+973">🇧🇭 +973 (Bahrain)</option>
                        <option value="+974">🇶🇦 +974 (Qatar)</option>
                        <option value="+975">🇧🇹 +975 (Bhutan)</option>
                        <option value="+976">🇲🇳 +976 (Mongolia)</option>
                        <option value="+977">🇳🇵 +977 (Nepal)</option>
                        <option value="+992">🇹🇯 +992 (Tajikistan)</option>
                        <option value="+993">🇹🇲 +993 (Turkmenistan)</option>
                        <option value="+994">🇦🇿 +994 (Azerbaijan)</option>
                        <option value="+995">🇬🇪 +995 (Georgia)</option>
                        <option value="+996">🇰🇬 +996 (Kyrgyzstan)</option>
                        <option value="+998">🇺🇿 +998 (Uzbekistan)</option>
                      </select>
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={passengerData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="801 234 5678"
                        className="form-input phone-input"
                        required
                      />
                    </div>
                  </div>

                  {/* Next of Kin */}
                  <div className="form-group">
                    <label htmlFor="nextOfKin" className="form-label">
                      Next of Kin <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="nextOfKin"
                      name="nextOfKin"
                      value={passengerData.nextOfKin}
                      onChange={handleInputChange}
                      placeholder="Enter next of kin full name"
                      className="form-input"
                      required
                    />
                  </div>

                  {/* Emergency Contact Number */}
                  <div className="form-group">
                    <label htmlFor="emergencyContact" className="form-label">
                      Emergency Contact Number <span className="required">*</span>
                    </label>
                    <div className="phone-input-wrapper">
                      <select
                        id="emergencyCountryCode"
                        name="emergencyCountryCode"
                        value={passengerData.emergencyCountryCode}
                        onChange={handleInputChange}
                        className="phone-country-code-select"
                      >
                        <option value="+1">🇺🇸 +1 (US/CA)</option>
                        <option value="+44">🇬🇧 +44 (UK)</option>
                        <option value="+234">🇳🇬 +234 (Nigeria)</option>
                        <option value="+233">🇬🇭 +233 (Ghana)</option>
                        <option value="+254">🇰🇪 +254 (Kenya)</option>
                        <option value="+27">🇿🇦 +27 (South Africa)</option>
                        <option value="+971">🇦🇪 +971 (UAE)</option>
                        <option value="+33">🇫🇷 +33 (France)</option>
                        <option value="+49">🇩🇪 +49 (Germany)</option>
                        <option value="+39">🇮🇹 +39 (Italy)</option>
                        <option value="+34">🇪🇸 +34 (Spain)</option>
                        <option value="+7">🇷🇺 +7 (Russia)</option>
                        <option value="+81">🇯🇵 +81 (Japan)</option>
                        <option value="+86">🇨🇳 +86 (China)</option>
                        <option value="+91">🇮🇳 +91 (India)</option>
                        <option value="+61">🇦🇺 +61 (Australia)</option>
                        <option value="+55">🇧🇷 +55 (Brazil)</option>
                        <option value="+52">🇲🇽 +52 (Mexico)</option>
                        <option value="+20">🇪🇬 +20 (Egypt)</option>
                        <option value="+251">🇪🇹 +251 (Ethiopia)</option>
                        <option value="+255">🇹🇿 +255 (Tanzania)</option>
                        <option value="+256">🇺🇬 +256 (Uganda)</option>
                        <option value="+250">🇷🇼 +250 (Rwanda)</option>
                        <option value="+225">🇨🇮 +225 (Ivory Coast)</option>
                        <option value="+221">🇸🇳 +221 (Senegal)</option>
                        <option value="+212">🇲🇦 +212 (Morocco)</option>
                        <option value="+213">🇩🇿 +213 (Algeria)</option>
                        <option value="+216">🇹🇳 +216 (Tunisia)</option>
                        <option value="+220">🇬🇲 +220 (Gambia)</option>
                        <option value="+232">🇸🇱 +232 (Sierra Leone)</option>
                        <option value="+229">🇧🇯 +229 (Benin)</option>
                        <option value="+226">🇧🇫 +226 (Burkina Faso)</option>
                        <option value="+227">🇳🇪 +227 (Niger)</option>
                        <option value="+228">🇹🇬 +228 (Togo)</option>
                        <option value="+235">🇹🇩 +235 (Chad)</option>
                        <option value="+236">🇨🇫 +236 (Central African Republic)</option>
                        <option value="+237">🇨🇲 +237 (Cameroon)</option>
                        <option value="+240">🇬🇶 +240 (Equatorial Guinea)</option>
                        <option value="+241">🇬🇦 +241 (Gabon)</option>
                        <option value="+242">🇨🇬 +242 (Congo)</option>
                        <option value="+243">🇨🇩 +243 (DR Congo)</option>
                        <option value="+244">🇦🇴 +244 (Angola)</option>
                        <option value="+245">🇬🇼 +245 (Guinea-Bissau)</option>
                        <option value="+246">🇮🇴 +246 (British Indian Ocean Territory)</option>
                        <option value="+248">🇸🇨 +248 (Seychelles)</option>
                        <option value="+249">🇸🇩 +249 (Sudan)</option>
                        <option value="+252">🇸🇴 +252 (Somalia)</option>
                        <option value="+253">🇩🇯 +253 (Djibouti)</option>
                        <option value="+257">🇧🇮 +257 (Burundi)</option>
                        <option value="+258">🇲🇿 +258 (Mozambique)</option>
                        <option value="+260">🇿🇲 +260 (Zambia)</option>
                        <option value="+261">🇲🇬 +261 (Madagascar)</option>
                        <option value="+262">🇷🇪 +262 (Réunion)</option>
                        <option value="+263">🇿🇼 +263 (Zimbabwe)</option>
                        <option value="+264">🇳🇦 +264 (Namibia)</option>
                        <option value="+265">🇲🇼 +265 (Malawi)</option>
                        <option value="+266">🇱🇸 +266 (Lesotho)</option>
                        <option value="+267">🇧🇼 +267 (Botswana)</option>
                        <option value="+268">🇸🇿 +268 (Eswatini)</option>
                        <option value="+269">🇰🇲 +269 (Comoros)</option>
                        <option value="+290">🇸🇭 +290 (Saint Helena)</option>
                        <option value="+291">🇪🇷 +291 (Eritrea)</option>
                        <option value="+297">🇦🇼 +297 (Aruba)</option>
                        <option value="+298">🇫🇴 +298 (Faroe Islands)</option>
                        <option value="+299">🇬🇱 +299 (Greenland)</option>
                        <option value="+350">🇬🇮 +350 (Gibraltar)</option>
                        <option value="+351">🇵🇹 +351 (Portugal)</option>
                        <option value="+352">🇱🇺 +352 (Luxembourg)</option>
                        <option value="+353">🇮🇪 +353 (Ireland)</option>
                        <option value="+354">🇮🇸 +354 (Iceland)</option>
                        <option value="+355">🇦🇱 +355 (Albania)</option>
                        <option value="+356">🇲🇹 +356 (Malta)</option>
                        <option value="+357">🇨🇾 +357 (Cyprus)</option>
                        <option value="+358">🇫🇮 +358 (Finland)</option>
                        <option value="+359">🇧🇬 +359 (Bulgaria)</option>
                        <option value="+370">🇱🇹 +370 (Lithuania)</option>
                        <option value="+371">🇱🇻 +371 (Latvia)</option>
                        <option value="+372">🇪🇪 +372 (Estonia)</option>
                        <option value="+373">🇲🇩 +373 (Moldova)</option>
                        <option value="+374">🇦🇲 +374 (Armenia)</option>
                        <option value="+375">🇧🇾 +375 (Belarus)</option>
                        <option value="+376">🇦🇩 +376 (Andorra)</option>
                        <option value="+377">🇲🇨 +377 (Monaco)</option>
                        <option value="+378">🇸🇲 +378 (San Marino)</option>
                        <option value="+380">🇺🇦 +380 (Ukraine)</option>
                        <option value="+381">🇷🇸 +381 (Serbia)</option>
                        <option value="+382">🇲🇪 +382 (Montenegro)</option>
                        <option value="+383">🇽🇰 +383 (Kosovo)</option>
                        <option value="+385">🇭🇷 +385 (Croatia)</option>
                        <option value="+386">🇸🇮 +386 (Slovenia)</option>
                        <option value="+387">🇧🇦 +387 (Bosnia and Herzegovina)</option>
                        <option value="+389">🇲🇰 +389 (North Macedonia)</option>
                        <option value="+420">🇨🇿 +420 (Czech Republic)</option>
                        <option value="+421">🇸🇰 +421 (Slovakia)</option>
                        <option value="+423">🇱🇮 +423 (Liechtenstein)</option>
                        <option value="+500">🇫🇰 +500 (Falkland Islands)</option>
                        <option value="+501">🇧🇿 +501 (Belize)</option>
                        <option value="+502">🇬🇹 +502 (Guatemala)</option>
                        <option value="+503">🇸🇻 +503 (El Salvador)</option>
                        <option value="+504">🇭🇳 +504 (Honduras)</option>
                        <option value="+505">🇳🇮 +505 (Nicaragua)</option>
                        <option value="+506">🇨🇷 +506 (Costa Rica)</option>
                        <option value="+507">🇵🇦 +507 (Panama)</option>
                        <option value="+508">🇵🇲 +508 (Saint Pierre and Miquelon)</option>
                        <option value="+509">🇭🇹 +509 (Haiti)</option>
                        <option value="+590">🇬🇵 +590 (Guadeloupe)</option>
                        <option value="+591">🇧🇴 +591 (Bolivia)</option>
                        <option value="+592">🇬🇾 +592 (Guyana)</option>
                        <option value="+593">🇪🇨 +593 (Ecuador)</option>
                        <option value="+594">🇬🇫 +594 (French Guiana)</option>
                        <option value="+595">🇵🇾 +595 (Paraguay)</option>
                        <option value="+596">🇲🇶 +596 (Martinique)</option>
                        <option value="+597">🇸🇷 +597 (Suriname)</option>
                        <option value="+598">🇺🇾 +598 (Uruguay)</option>
                        <option value="+599">🇧🇶 +599 (Caribbean Netherlands)</option>
                        <option value="+670">🇹🇱 +670 (East Timor)</option>
                        <option value="+672">🇦🇶 +672 (Antarctica)</option>
                        <option value="+673">🇧🇳 +673 (Brunei)</option>
                        <option value="+674">🇳🇷 +674 (Nauru)</option>
                        <option value="+675">🇵🇬 +675 (Papua New Guinea)</option>
                        <option value="+676">🇹🇴 +676 (Tonga)</option>
                        <option value="+677">🇸🇧 +677 (Solomon Islands)</option>
                        <option value="+678">🇻🇺 +678 (Vanuatu)</option>
                        <option value="+679">🇫🇯 +679 (Fiji)</option>
                        <option value="+680">🇵🇼 +680 (Palau)</option>
                        <option value="+681">🇼🇫 +681 (Wallis and Futuna)</option>
                        <option value="+682">🇨🇰 +682 (Cook Islands)</option>
                        <option value="+683">🇳🇺 +683 (Niue)</option>
                        <option value="+685">🇼🇸 +685 (Samoa)</option>
                        <option value="+686">🇰🇮 +686 (Kiribati)</option>
                        <option value="+687">🇳🇨 +687 (New Caledonia)</option>
                        <option value="+688">🇹🇻 +688 (Tuvalu)</option>
                        <option value="+689">🇵🇫 +689 (French Polynesia)</option>
                        <option value="+690">🇹🇰 +690 (Tokelau)</option>
                        <option value="+691">🇫🇲 +691 (Micronesia)</option>
                        <option value="+692">🇲🇭 +692 (Marshall Islands)</option>
                        <option value="+850">🇰🇵 +850 (North Korea)</option>
                        <option value="+852">🇭🇰 +852 (Hong Kong)</option>
                        <option value="+853">🇲🇴 +853 (Macau)</option>
                        <option value="+855">🇰🇭 +855 (Cambodia)</option>
                        <option value="+856">🇱🇦 +856 (Laos)</option>
                        <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                        <option value="+886">🇹🇼 +886 (Taiwan)</option>
                        <option value="+960">🇲🇻 +960 (Maldives)</option>
                        <option value="+961">🇱🇧 +961 (Lebanon)</option>
                        <option value="+962">🇯🇴 +962 (Jordan)</option>
                        <option value="+963">🇸🇾 +963 (Syria)</option>
                        <option value="+964">🇮🇶 +964 (Iraq)</option>
                        <option value="+965">🇰🇼 +965 (Kuwait)</option>
                        <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                        <option value="+967">🇾🇪 +967 (Yemen)</option>
                        <option value="+968">🇴🇲 +968 (Oman)</option>
                        <option value="+970">🇵🇸 +970 (Palestine)</option>
                        <option value="+971">🇦🇪 +971 (UAE)</option>
                        <option value="+972">🇮🇱 +972 (Israel)</option>
                        <option value="+973">🇧🇭 +973 (Bahrain)</option>
                        <option value="+974">🇶🇦 +974 (Qatar)</option>
                        <option value="+975">🇧🇹 +975 (Bhutan)</option>
                        <option value="+976">🇲🇳 +976 (Mongolia)</option>
                        <option value="+977">🇳🇵 +977 (Nepal)</option>
                        <option value="+992">🇹🇯 +992 (Tajikistan)</option>
                        <option value="+993">🇹🇲 +993 (Turkmenistan)</option>
                        <option value="+994">🇦🇿 +994 (Azerbaijan)</option>
                        <option value="+995">🇬🇪 +995 (Georgia)</option>
                        <option value="+996">🇰🇬 +996 (Kyrgyzstan)</option>
                        <option value="+998">🇺🇿 +998 (Uzbekistan)</option>
                      </select>
                      <input
                        type="tel"
                        id="emergencyContact"
                        name="emergencyContact"
                        value={passengerData.emergencyContact}
                        onChange={handleInputChange}
                        placeholder="801 234 5678"
                        className="form-input phone-input"
                        required
                      />
                    </div>
                  </div>

                  {/* Terms and Conditions Checkbox */}
                  <div className="terms-checkbox-container">
                    <label className="terms-checkbox-label">
                      <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="terms-checkbox"
                        required
                      />
                      <span className="terms-checkbox-text">
                        I accept the <Link to="/terms-and-conditions" state={{ passengerData, flight, additionalOptions, acceptedTerms }} className="terms-link">Terms and Conditions</Link>
                      </span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button 
                    type="submit" 
                    className="form-submit-button"
                    disabled={!acceptedTerms}
                  >
                    Continue to Payment
                  </button>
                </form>
              </div>
            </main>

            {/* Additional Options Sidebar */}
            <aside className="additional-options-sidebar">
              {(flight.baggage && (() => {
                // Check if extra baggage pricing is available
                let hasExtraBaggage = false;
                if (flight.rawOffer?.available_services && Array.isArray(flight.rawOffer.available_services)) {
                  const baggageServices = flight.rawOffer.available_services.filter((s) => 
                    (s.type === 'baggage' || s.service_type === 'baggage') &&
                    s.total_amount &&
                    parseFloat(s.total_amount) > 0 &&
                    s.metadata?.type === 'checked'
                  );
                  hasExtraBaggage = baggageServices.length > 0;
                }
                return hasExtraBaggage || flight.numberOfBookableSeats;
              })()) && (
                <h3 className="options-title">Additional Options</h3>
              )}
              
              <div className="options-list">
                {/* Extra Baggage - Only show if extra baggage pricing is available from API */}
                {flight.baggage && (() => {
                  // Check for extra baggage pricing from API first
                  let extraBaggagePrice = null;
                  let extraBaggageWeight = null;
                  
                  // Check rawOffer for available_services with baggage pricing
                  if (flight.rawOffer?.available_services && Array.isArray(flight.rawOffer.available_services)) {
                    const baggageServices = flight.rawOffer.available_services.filter((s) => 
                      (s.type === 'baggage' || s.service_type === 'baggage') &&
                      s.total_amount &&
                      parseFloat(s.total_amount) > 0 &&
                      s.metadata?.type === 'checked'
                    );
                    
                    if (baggageServices.length > 0) {
                      const baggageService = baggageServices[0];
                      extraBaggagePrice = parseFloat(baggageService.total_amount) || null;
                      extraBaggageWeight = baggageService.metadata?.maximum_weight_kg || 
                                         baggageService.metadata?.weight_kg || 
                                         null;
                      
                      // Convert to NGN if needed
                      if (extraBaggagePrice && baggageService.total_currency && baggageService.total_currency !== 'NGN') {
                        const currencyRates = {
                          'USD': 1500, 'EUR': 1650, 'GBP': 1900, 'AED': 410,
                          'ZAR': 80, 'GHS': 100, 'KES': 10, 'NGN': 1
                        };
                        const rate = currencyRates[baggageService.total_currency.toUpperCase()] || 1500;
                        extraBaggagePrice = Math.round(extraBaggagePrice * rate);
                      }
                    }
                  }

                  // Only show the section if extra baggage pricing is available from API
                  if (!extraBaggagePrice) {
                    return null;
                  }

                  // Extract current baggage from API
                  let currentBaggageText = '';
                  if (typeof flight.baggage === 'object') {
                    // Format baggage from API object
                    const checkedBaggage = flight.baggage.checked || '';
                    const carryOnBaggage = flight.baggage.carryOn || '';
                    if (checkedBaggage && carryOnBaggage) {
                      currentBaggageText = `${checkedBaggage} checked, ${carryOnBaggage} carry-on`;
                    } else if (checkedBaggage) {
                      currentBaggageText = checkedBaggage;
                    } else if (carryOnBaggage) {
                      currentBaggageText = carryOnBaggage;
                    } else {
                      currentBaggageText = 'Baggage included';
                    }
                  } else {
                    // Use baggage string directly from API
                    currentBaggageText = flight.baggage;
                  }

                  return (
                    <div 
                      className={`option-card ${additionalOptions.extraBaggage ? 'selected' : ''}`}
                      onClick={() => handleOptionChange('extraBaggage')}
                    >
                      <div className="option-icon">🧳</div>
                      <div className="option-content">
                        <h4 className="option-name">Extra Baggage</h4>
                        <p className="option-description">
                          {flight.isRoundTrip ? (
                            <>
                              <div style={{ marginBottom: '0.5rem' }}>
                                <strong>Outbound:</strong> {currentBaggageText} included
                                {extraBaggageWeight && (
                                  <> • Add {extraBaggageWeight}kg for {formatPrice(extraBaggagePrice)}</>
                                )}
                                {!extraBaggageWeight && (
                                  <> • Add extra baggage for {formatPrice(extraBaggagePrice)}</>
                                )}
                              </div>
                              <div>
                                <strong>Return:</strong> {currentBaggageText} included
                                {extraBaggageWeight && (
                                  <> • Add {extraBaggageWeight}kg for {formatPrice(extraBaggagePrice)}</>
                                )}
                                {!extraBaggageWeight && (
                                  <> • Add extra baggage for {formatPrice(extraBaggagePrice)}</>
                                )}
                              </div>
                            </>
                          ) : (
                            <>
                              Current: {currentBaggageText} included
                              {extraBaggageWeight && (
                                <> • Add {extraBaggageWeight}kg for {formatPrice(extraBaggagePrice)}</>
                              )}
                              {!extraBaggageWeight && (
                                <> • Add extra baggage for {formatPrice(extraBaggagePrice)}</>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                      <div className="option-checkbox">
                        <input
                          type="checkbox"
                          checked={additionalOptions.extraBaggage}
                          onChange={() => handleOptionChange('extraBaggage')}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* Seat Selection - Only show if seat availability data is available from API */}
                {flight.numberOfBookableSeats && (
                  <div 
                    className={`option-card ${additionalOptions.seatSelection ? 'selected' : ''}`}
                    onClick={() => handleOptionChange('seatSelection')}
                  >
                    <div className="option-icon">🪑</div>
                    <div className="option-content">
                      <h4 className="option-name">Seat Selection</h4>
                      <p className="option-description">
                        {flight.numberOfBookableSeats} seats available • Choose your preferred seat
                      </p>
                    </div>
                    <div className="option-checkbox">
                      <input
                        type="checkbox"
                        checked={additionalOptions.seatSelection}
                        onChange={() => handleOptionChange('seatSelection')}
                      />
                    </div>
                  </div>
                )}

                {/* Show message if no additional options are available */}
                {!flight.baggage && !flight.numberOfBookableSeats && (
                  <div className="no-options-message">
                    <p>No additional options available for this flight.</p>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default PassengerInfoPage;

